/**
 * The recorded segments of each camera, from its high stream: one member for each camera, so a
 * timeline and the scene that show the same camera share one list and one request.
 *
 * Each reader polls at its own interval, and the member polls at the shortest interval of the
 * mounted readers. The first load asks for the full list. Each later poll asks only for the tail,
 * from the start of the last known segment, so the open segment at the end refreshes too, and
 * the tail goes onto the held list. A poll that brings nothing new keeps the same array.
 *
 * Retention removes segments from the start of the archive, and the tail poll never reads them
 * again. Thus, the removed segments stay until the member loads again. Retention moves on a
 * scale of hours, so this is acceptable.
 *
 * The reads are derived atoms that give only the list. The snapshot is a new object at each poll,
 * but a derived atom that gives the same array does not notify its readers, so a poll with
 * nothing new causes no render. A reader calls a read in render, so each read is cached: a new
 * atom at each render subscribes again and gives a new value.
 *
 * The archive start of a camera scope is the earliest first segment of its cameras, read from
 * the same members, so a list panel and the timeline of a camera share its request.
 */

import { type Atom, atom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import { getScopeKey } from '@/lib/scopeKey';
import { parseCompactSegments, spliceTailSegments } from '@/lib/segments';
import { streamKeyOf } from '@/player/usePlayerStreams';
import type {
    ArchiveCamera, ArchiveStartSnapshot, PlayerDevice, RecordedSegment,
} from '@/types/player';
import type { LoadState } from '@/types/snapshot';

interface SegmentsSnapshot extends LoadState {
    segments: RecordedSegment[];
    /** True from the first load that succeeded. */
    isLoaded: boolean;
}

interface SegmentsPage {
    /** The page starts at the last held segment. */
    isTail: boolean;
    segments: RecordedSegment[];
}

type LayoutSegments = ReadonlyMap<string, RecordedSegment[]>;

interface LayoutRead {
    key: string;
    listAtom: Atom<RecordedSegment[] | null>;
}

interface LayoutEntry {
    reads: readonly LayoutRead[];
    layoutAtom: Atom<LayoutSegments>;
}

interface ArchiveStartEntry {
    members: readonly Atom<SegmentsSnapshot>[];
    startAtom: Atom<ArchiveStartSnapshot>;
}

const NO_ARCHIVE_START: ArchiveStartSnapshot = { startMs: null, isLoading: false, error: null };

const PENDING: SegmentsSnapshot = { segments: [], isLoaded: false, isLoading: true, error: null };

const cameras = queryAtoms<ArchiveCamera, SegmentsSnapshot, SegmentsPage>({
    getRequestKey: ({ serverId, cameraId }): string => getScopeKey({ serverId, cameraId }),
    getPending: (): SegmentsSnapshot => PENDING,
    load: async ({ serverId, cameraId }, previousSnapshot, signal): Promise<SegmentsPage> => {
        const lastSegment = previousSnapshot?.isLoaded === true
            ? previousSnapshot.segments.at(-1)
            : undefined;
        if (lastSegment === undefined) {
            const segments = await api.getSegments(cameraId, { serverId, signal });
            return { isTail: false, segments: parseCompactSegments(segments) };
        }
        const segments = await api.getSegments(cameraId, {
            serverId, startTimeMs: lastSegment.startTimeMs, signal,
        });
        return { isTail: true, segments: parseCompactSegments(segments) };
    },
    merge: (previousSnapshot, { isTail, segments }): SegmentsSnapshot => ({
        segments: isTail && previousSnapshot !== null
            ? spliceTailSegments(previousSnapshot.segments, segments)
            : segments,
        isLoaded: true,
        isLoading: false,
        error: null,
    }),
});

const noSegmentsAtom = atom<RecordedSegment[] | null>(null);
const noLayoutSegmentsAtom = atom<LayoutSegments>(new Map<string, RecordedSegment[]>());
const noArchiveStartAtom = atom<ArchiveStartSnapshot>(NO_ARCHIVE_START);

const listAtoms = new WeakMap<Atom<SegmentsSnapshot>, Atom<RecordedSegment[] | null>>();
const layoutAtoms = new WeakMap<readonly PlayerDevice[], Map<number, LayoutEntry>>();
const archiveStartAtoms = new WeakMap<readonly ArchiveCamera[], ArchiveStartEntry>();

function isCurrent<T>(cachedItems: readonly T[], currentItems: readonly T[]): boolean {
    return cachedItems.length === currentItems.length &&
        cachedItems.every((one, index) => one === currentItems[index]);
}

function isSameArchiveStart(one: ArchiveStartSnapshot, other: ArchiveStartSnapshot): boolean {
    return one.startMs === other.startMs && one.isLoading === other.isLoading &&
        one.error === other.error;
}

function getListAtom(snapshotAtom: Atom<SegmentsSnapshot>): Atom<RecordedSegment[] | null> {
    const existingAtom = listAtoms.get(snapshotAtom);
    if (existingAtom !== undefined) {
        return existingAtom;
    }
    const createdAtom = atom((get): RecordedSegment[] | null => {
        const { isLoaded, segments } = get(snapshotAtom);
        return isLoaded ? segments : null;
    });
    listAtoms.set(snapshotAtom, createdAtom);
    return createdAtom;
}

/** The camera's list, null until its first load; while mounted, it polls every `intervalMs`. */
export function liveSegmentsAtom(
    camera: ArchiveCamera | null, intervalMs: number,
): Atom<RecordedSegment[] | null> {
    return camera === null ? noSegmentsAtom : getListAtom(cameras.getLive(camera, intervalMs));
}

/** The camera's list, null until its first load, with no poll. */
export function segmentsAtom(camera: ArchiveCamera | null): Atom<RecordedSegment[] | null> {
    return camera === null ? noSegmentsAtom : getListAtom(cameras.getMember(camera));
}

/**
 * The list of each of `devices` by `streamKeyOf`, polled every `intervalMs`; a device is absent
 * until its first load. The callers memoize `devices`, so the array keys the atom. The atom
 * stays only while it reads the current members: a cached atom that mounts again after a
 * removal would mount the removed members again.
 */
export function layoutSegmentsAtom(
    devices: readonly PlayerDevice[], intervalMs: number,
): Atom<LayoutSegments> {
    if (devices.length === 0) {
        return noLayoutSegmentsAtom;
    }
    const reads = devices.map(({ serverId, deviceId }): LayoutRead => ({
        key: streamKeyOf(serverId, deviceId),
        listAtom: liveSegmentsAtom({ serverId, cameraId: deviceId }, intervalMs),
    }));
    let byInterval = layoutAtoms.get(devices);
    if (byInterval === undefined) {
        byInterval = new Map();
        layoutAtoms.set(devices, byInterval);
    }
    const existingLayoutEntry = byInterval.get(intervalMs);
    if (existingLayoutEntry !== undefined && isCurrent(
        existingLayoutEntry.reads.map(({ listAtom }) => listAtom),
        reads.map(({ listAtom }) => listAtom))) {
        return existingLayoutEntry.layoutAtom;
    }
    const layoutAtom = atom((get): LayoutSegments => {
        const lists = new Map<string, RecordedSegment[]>();
        for (const { key, listAtom } of reads) {
            const list = get(listAtom);
            if (list !== null) {
                lists.set(key, list);
            }
        }
        return lists;
    });
    byInterval.set(intervalMs, { reads, layoutAtom });
    return layoutAtom;
}

export function archiveStartAtom(scope: readonly ArchiveCamera[]): Atom<ArchiveStartSnapshot> {
    if (scope.length === 0) {
        return noArchiveStartAtom;
    }
    const members = scope.map((camera) => cameras.getMember(camera));
    const existingEntry = archiveStartAtoms.get(scope);
    if (existingEntry !== undefined && isCurrent(existingEntry.members, members)) {
        return existingEntry.startAtom;
    }
    let previousStart: ArchiveStartSnapshot | null = null;
    const startAtom = atom((get): ArchiveStartSnapshot => {
        const snapshots = members.map((member) => get(member));
        const startValues = snapshots.flatMap(({ isLoaded, segments }) => {
            const firstStartMs = segments.at(0)?.startTimeMs;
            return isLoaded && firstStartMs !== undefined ? [firstStartMs] : [];
        });
        const start: ArchiveStartSnapshot = {
            startMs: startValues.length > 0 ? Math.min(...startValues) : null,
            isLoading: snapshots.some(({ isLoaded, isLoading, error }) =>
                !isLoaded && isLoading && error === null),
            error: snapshots.find(({ error }) => error !== null)?.error ?? null,
        };
        if (previousStart === null || !isSameArchiveStart(previousStart, start)) {
            previousStart = start;
        }
        return previousStart;
    });
    archiveStartAtoms.set(scope, { members, startAtom });
    return startAtom;
}
